import { INTERVAL_SECONDS } from '../config/timeframes.js';
import type { CandleTiming, ChartInterval, Horizon } from '../types/market.js';
import { clamp } from './number.js';

function weekOpen(timestamp: number): number {
  const date = new Date(timestamp);
  const utcDay = date.getUTCDay();
  const daysSinceMonday = (utcDay + 6) % 7;
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - daysSinceMonday);
}

export function getCandleTiming(
  interval: ChartInterval | Horizon,
  timestamp = Date.now(),
  serverOffsetMs = 0,
): CandleTiming {
  const exchangeTime = timestamp + serverOffsetMs;
  const durationMs = INTERVAL_SECONDS[interval] * 1_000;
  const openTime = interval === '1w' ? weekOpen(exchangeTime) : Math.floor(exchangeTime / durationMs) * durationMs;
  const closeTime = openTime + durationMs;
  const elapsedMs = clamp(exchangeTime - openTime, 0, durationMs);
  const remainingMs = Math.max(0, closeTime - exchangeTime);
  const maturity = clamp((elapsedMs / durationMs) * 100, 0, 100);

  const phase: CandleTiming['phase'] =
    maturity < 15 ? 'opening' : maturity < 50 ? 'developing' : maturity < 80 ? 'mature' : maturity < 95 ? 'closing' : 'final';

  return {
    openTime,
    closeTime,
    elapsedMs,
    remainingMs,
    maturity,
    phase,
    freshIssuanceAllowed: maturity < 88,
  };
}

export function candidateTimingNote(timing: CandleTiming): string {
  switch (timing.phase) {
    case 'opening':
      return 'New candle is still stabilizing; early direction can change quickly.';
    case 'developing':
      return 'Candle is developing and can support an early observation.';
    case 'mature':
      return 'Candle has enough history for stronger contextual analysis.';
    case 'closing':
      return 'Candle is approaching close; fresh same-horizon candidates require stricter confirmation.';
    case 'final':
      return 'Fresh same-horizon issuance is blocked until the next candle transition is assessed.';
  }
}
